/**
 * Agent workspace state.
 *
 * The server owns agent and call state. This hook mirrors it, re-reads on every
 * realtime notification and on reconnect, and surfaces the server's own error
 * message when an action is refused.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, api } from '@/lib/api/client';
import type {
  ActivityEntry,
  AgentStateResponse,
  CallDetail,
  ContactDetail,
  DirectoryEntry,
  DispositionOption,
  MyWork,
  RecentAgentCall,
  TimelineEntry,
} from '@/lib/api/types';
import { connectRealtime } from '@/lib/realtime';
import type { ConnectionStatus } from '@/lib/realtime';

export interface WorkspaceState {
  agent: AgentStateResponse | null;
  call: CallDetail | null;
  contact: ContactDetail | null;
  contactActivity: ActivityEntry[];
  timeline: TimelineEntry[];
  selectedContactId: string | null;
  selectedCallId: string | null;
  work: MyWork | null;
  recentCalls: RecentAgentCall[];
  dispositions: DispositionOption[];
  directory: DirectoryEntry[];
  connection: ConnectionStatus;
  lastError: { message: string; code: string; details: Record<string, unknown> } | null;
  loading: boolean;
  staleSince: number | null;
}

const INITIAL: WorkspaceState = {
  agent: null,
  call: null,
  contact: null,
  contactActivity: [],
  timeline: [],
  selectedContactId: null,
  selectedCallId: null,
  work: null,
  recentCalls: [],
  dispositions: [],
  directory: [],
  connection: 'connecting',
  lastError: null,
  loading: true,
  staleSince: null,
};

export function useAgentWorkspace() {
  const [state, setState] = useState<WorkspaceState>(INITIAL);
  const currentCallId = useRef<string | null>(null);
  const selectedContactId = useRef<string | null>(null);
  const selectedCallId = useRef<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const [agent, recentCalls] = await Promise.all([
        api.get<AgentStateResponse>('/agent/state'),
        api.get<RecentAgentCall[]>('/agent/recent-calls?limit=500').catch(() => null),
      ]);
      let call: CallDetail | null = null;
      let contact: ContactDetail | null = null;
      let contactActivity: ActivityEntry[] = [];
      let timeline: TimelineEntry[] = [];

      if (agent.current_call_id) {
        call = await api.get<CallDetail>(`/agent/calls/${agent.current_call_id}`);
        timeline = await api.get<TimelineEntry[]>(`/agent/calls/${agent.current_call_id}/timeline`);
        if (call.contact_id) {
          [contact, contactActivity] = await Promise.all([
            api.get<ContactDetail>(`/crm/contacts/${call.contact_id}`),
            api.get<ActivityEntry[]>(`/crm/contacts/${call.contact_id}/timeline`),
          ]);
          selectedContactId.current = call.contact_id;
        }
        selectedCallId.current = call.id;
      } else {
        const availableCalls = recentCalls ?? [];
        const selectedRecentCall =
          availableCalls.find((item) => item.id === selectedCallId.current) ??
          (!selectedContactId.current
            ? availableCalls.find((item) => item.contact_id !== null)
            : undefined);

        if (selectedRecentCall?.contact_id) {
          selectedCallId.current = selectedRecentCall.id;
          selectedContactId.current = selectedRecentCall.contact_id;
        }

        if (selectedContactId.current) {
          const requests: [
            Promise<ContactDetail>,
            Promise<ActivityEntry[]>,
            Promise<TimelineEntry[]>,
          ] = [
            api.get<ContactDetail>(`/crm/contacts/${selectedContactId.current}`),
            api.get<ActivityEntry[]>(`/crm/contacts/${selectedContactId.current}/timeline`),
            selectedCallId.current
              ? api.get<TimelineEntry[]>(`/agent/calls/${selectedCallId.current}/timeline`)
              : Promise.resolve([]),
          ];
          [contact, contactActivity, timeline] = await Promise.all(requests);
        }
      }
      currentCallId.current = agent.current_call_id;

      setState((previous) => ({
        ...previous,
        agent,
        call,
        contact,
        contactActivity,
        timeline,
        selectedContactId: selectedContactId.current,
        selectedCallId: selectedCallId.current,
        recentCalls: recentCalls ?? previous.recentCalls,
        // REST is the authoritative connection to the live service. This
        // keeps the status truthful even when a WebSocket upgrade is delayed
        // by a reverse proxy; realtime events still move it to reconnecting
        // or offline when the socket actually drops.
        connection: 'online',
        loading: false,
        staleSince: null,
      }));
    } catch (error) {
      setState((previous) => ({
        ...previous,
        loading: false,
        // Keep showing the last good data, but mark it stale rather than lying.
        staleSince: previous.staleSince ?? Date.now(),
        lastError:
          error instanceof ApiError
            ? { message: error.message, code: error.code, details: error.details }
            : { message: 'Unable to reach the server', code: 'network_error', details: {} },
      }));
    }
  }, []);

  const refreshReference = useCallback(async () => {
    const [work, dispositions, directory] = await Promise.all([
      api.get<MyWork>('/crm/my-work').catch(() => null),
      api.get<DispositionOption[]>('/agent/dispositions').catch(() => []),
      api.get<DirectoryEntry[]>('/agent/directory').catch(() => []),
    ]);
    setState((previous) => ({
      ...previous,
      work: work ?? previous.work,
      dispositions,
      directory,
    }));
  }, []);

  useEffect(() => {
    void refresh();
    void refreshReference();
    const disconnect = connectRealtime(['agent', 'calls', 'notifications'], {
      onMessage: () => {
        void refresh();
      },
      onStatus: (connection) => setState((previous) => ({ ...previous, connection })),
      onResync: () => {
        void refresh();
        void refreshReference();
      },
    });
    // A slow tick keeps timers honest even if no event arrives.
    const timer = setInterval(() => {
      void refresh();
    }, 10000);
    return () => {
      disconnect();
      clearInterval(timer);
    };
  }, [refresh, refreshReference]);

  const run = useCallback(
    async (action: () => Promise<unknown>) => {
      setState((previous) => ({ ...previous, lastError: null }));
      try {
        await action();
      } catch (error) {
        if (error instanceof ApiError) {
          setState((previous) => ({
            ...previous,
            lastError: { message: error.message, code: error.code, details: error.details },
          }));
        } else {
          setState((previous) => ({
            ...previous,
            lastError: { message: 'Action failed', code: 'network_error', details: {} },
          }));
        }
      } finally {
        await refresh();
      }
    },
    [refresh],
  );

  const callId = state.call?.id ?? state.agent?.current_call_id ?? null;

  const selectRecord = useCallback(async (contactId: string, historicalCallId?: string) => {
    selectedContactId.current = contactId;
    selectedCallId.current = historicalCallId ?? null;
    setState((previous) => ({ ...previous, lastError: null }));
    try {
      const [contact, contactActivity, timeline] = await Promise.all([
        api.get<ContactDetail>(`/crm/contacts/${contactId}`),
        api.get<ActivityEntry[]>(`/crm/contacts/${contactId}/timeline`),
        historicalCallId
          ? api.get<TimelineEntry[]>(`/agent/calls/${historicalCallId}/timeline`)
          : Promise.resolve([]),
      ]);
      setState((previous) => ({
        ...previous,
        contact,
        contactActivity,
        timeline,
        selectedContactId: contactId,
        selectedCallId: historicalCallId ?? null,
      }));
    } catch (error) {
      setState((previous) => ({
        ...previous,
        lastError:
          error instanceof ApiError
            ? { message: error.message, code: error.code, details: error.details }
            : { message: 'Unable to load the customer record', code: 'network_error', details: {} },
      }));
    }
  }, []);

  const addContactNote = useCallback(async (contactId: string, body: string) => {
    setState((previous) => ({ ...previous, lastError: null }));
    try {
      await api.post(`/crm/contacts/${contactId}/comments`, { body, is_internal: true });
      const contactActivity = await api.get<ActivityEntry[]>(
        `/crm/contacts/${contactId}/timeline`,
      );
      setState((previous) => ({ ...previous, contactActivity }));
      return true;
    } catch (error) {
      setState((previous) => ({
        ...previous,
        lastError:
          error instanceof ApiError
            ? { message: error.message, code: error.code, details: error.details }
            : { message: 'Unable to save the lead note', code: 'network_error', details: {} },
      }));
      return false;
    }
  }, []);

  return {
    state,
    refresh,
    actions: {
      selectRecord,
      addContactNote,
      dial: (phoneE164: string) =>
        run(() => api.post('/agent/outbound', { phone_e164: phoneE164 })),
      dialContact: (contactId: string) =>
        run(() => api.post('/agent/outbound', { contact_id: contactId })),
      setAgentState: (agentState: string, reason?: string) =>
        run(() => api.post('/agent/state', { state: agentState, reason: reason ?? null })),
      answer: () => run(() => api.post(`/agent/calls/${callId}/answer`)),
      hold: () => run(() => api.post(`/agent/calls/${callId}/hold`)),
      resume: () => run(() => api.post(`/agent/calls/${callId}/resume`)),
      mute: (muted: boolean) => run(() => api.post(`/agent/calls/${callId}/mute`, { muted })),
      dtmf: (digits: string) => run(() => api.post(`/agent/calls/${callId}/dtmf`, { digits })),
      pauseRecording: (paused: boolean, reason = 'pci') =>
        run(() => api.post(`/agent/calls/${callId}/recording`, { paused, reason })),
      startTransfer: (toUserId: string, warm: boolean) =>
        run(() =>
          api.post(`/agent/calls/${callId}/transfer`, {
            to_user_id: toUserId,
            transfer_type: warm ? 'warm' : 'blind',
          }),
        ),
      completeTransfer: (consultLegId: string) =>
        run(() =>
          api.post(`/agent/calls/${callId}/transfer/complete`, { consult_leg_id: consultLegId }),
        ),
      conference: (toAddress: string) =>
        run(() => api.post(`/agent/calls/${callId}/conference`, { to_address: toAddress })),
      leave: () => run(() => api.post(`/agent/calls/${callId}/leave`)),
      hangup: () => run(() => api.post(`/agent/calls/${callId}/hangup`)),
      submitDisposition: (payload: {
        disposition_id: string;
        notes: string;
        callback_at: string | null;
      }) => run(() => api.post(`/agent/calls/${callId}/disposition`, payload)),
      completeAcw: () => run(() => api.post('/agent/acw/complete')),
    },
  };
}
